import { tr } from '@/lib/i18n-core';
import { Plus, X } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { Button, Checkbox, DatePicker, Field, Select } from '@/components/ui/primitives';
import { SUPER_ADMIN, type Role, type RoleRef } from './types';

export interface AssignmentRow {
  rowKey: string;
  roleId: string;
  branchId: string;
  temporary: boolean;
  until: string;
  originalUntil: string;
  originalExpiresAt: string | null;
}

export interface BranchOption {
  id: string;
  name: string;
}

let assignmentRowSeq = 0;

function nextRowKey(): string {
  assignmentRowSeq += 1;
  return `row-${assignmentRowSeq}`;
}

export function assignmentRowsOf(roles: readonly RoleRef[]): AssignmentRow[] {
  return roles.map((role) => {
    const until = role.expiresAt ? role.expiresAt.slice(0, 10) : '';
    return {
      rowKey: nextRowKey(),
      roleId: role.id,
      branchId: role.branchId ?? '',
      temporary: Boolean(role.expiresAt),
      until,
      originalUntil: until,
      originalExpiresAt: role.expiresAt,
    };
  });
}

export function assignmentPayload(row: AssignmentRow) {
  const expiresAt = !row.temporary || !row.until
    ? null
    : row.until === row.originalUntil && row.originalExpiresAt
      ? row.originalExpiresAt
      : new Date(`${row.until}T23:59:59`).toISOString();
  return { roleId: row.roleId, branchId: row.branchId || null, expiresAt };
}

export function useBranchScope(branches: readonly BranchOption[]) {
  const { isSuperAdmin, user: session } = useAuth();
  const actorHospitalWide = isSuperAdmin || Boolean(session?.roles.some((role) => role.branchId === null));
  const allowedBranchIds = new Set((session?.branches ?? []).map((branch) => branch.id));
  const pickableBranches = actorHospitalWide ? [...branches] : branches.filter((branch) => allowedBranchIds.has(branch.id));
  const showBranchPicker = branches.length > 1 || !actorHospitalWide;
  const branchName = (id: string) => branches.find((branch) => branch.id === id)?.name ?? '';
  const newAssignmentBranch = () => {
    if (actorHospitalWide) return '';
    const preferred = session?.defaultBranchId && allowedBranchIds.has(session.defaultBranchId) ? session.defaultBranchId : '';
    return preferred || pickableBranches[0]?.id || '';
  };
  return { actorHospitalWide, allowedBranchIds, pickableBranches, showBranchPicker, branchName, newAssignmentBranch };
}

export function RoleAssignmentsField({
  rows,
  onChange,
  roles,
  branches,
  editingSelf,
}: {
  rows: AssignmentRow[];
  onChange: (rows: AssignmentRow[]) => void;
  roles: Role[];
  branches: readonly BranchOption[];
  editingSelf: boolean;
}) {
  const { isSuperAdmin } = useAuth();
  const { actorHospitalWide, pickableBranches, showBranchPicker, branchName, newAssignmentBranch } = useBranchScope(branches);
  const superAdminRoleId = roles.find((role) => role.code === SUPER_ADMIN)?.id;
  const assignableRoles = roles.filter((role) => (role.code !== SUPER_ADMIN || isSuperAdmin) && (role.state ?? 'live') === 'live');

  const updateRow = (rowKey: string, change: Partial<AssignmentRow>) =>
    onChange(
      rows.map((row) => {
        if (row.rowKey !== rowKey) return row;
        const next = { ...row, ...change };
        return next.roleId === superAdminRoleId ? { ...next, branchId: '' } : next;
      }),
    );

  return (
    <div className="sm:col-span-2">
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-medium">{tr('admin', 'roles')}</p>
        {!editingSelf && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              onChange([
                ...rows,
                {
                  rowKey: nextRowKey(),
                  roleId: '',
                  branchId: newAssignmentBranch(),
                  temporary: false,
                  until: '',
                  originalUntil: '',
                  originalExpiresAt: null,
                },
              ])
            }
          >
            <Plus className="h-4 w-4" /> {tr('admin', 'add_a_role')}
          </Button>
        )}
      </div>
      {editingSelf && (
        <p className="mb-2 text-xs text-muted-foreground">{tr('admin', 'you_cant_change_your_own_roles_ask_another')}</p>
      )}
      {rows.length === 0 && <p className="text-xs text-muted-foreground">{tr('admin', 'no_role_yet')}</p>}
      <div className="space-y-2">
        {rows.map((row) => {
          const isSuperAdminRow = row.roleId === superAdminRoleId;
          return (
            <div key={row.rowKey} className="flex flex-wrap items-end gap-2 rounded-md border p-2">
              <Field label={tr('admin', 'role_2')} className="min-w-[10rem] flex-1">
                <Select
                  value={row.roleId}
                  disabled={editingSelf}
                  onChange={(e) => updateRow(row.rowKey, { roleId: e.target.value })}
                >
                  <option value="">{tr('admin', 'choose_a_role')}</option>
                  {(assignableRoles.some((role) => role.id === row.roleId) ? assignableRoles : roles).map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </Select>
              </Field>
              {showBranchPicker && (
                <Field label={tr('admin', 'branch')} className="min-w-[9rem] flex-1">
                  <Select
                    value={row.branchId}
                    disabled={editingSelf || isSuperAdminRow}
                    onChange={(e) => updateRow(row.rowKey, { branchId: e.target.value })}
                  >
                    {(actorHospitalWide || isSuperAdminRow) && <option value="">{tr('admin', 'all_branches')}</option>}
                    {!actorHospitalWide && !row.branchId && <option value="">{tr('admin', 'choose_a_branch_2')}</option>}
                    {(row.branchId && !pickableBranches.some((branch) => branch.id === row.branchId)
                      ? [...pickableBranches, { id: row.branchId, name: branchName(row.branchId) || 'Another branch' }]
                      : pickableBranches
                    ).map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <div className="pb-2">
                <Checkbox
                  checked={row.temporary}
                  disabled={editingSelf}
                  onCheckedChange={(checked) => updateRow(row.rowKey, { temporary: checked })}
                  label={tr('admin', 'temporary')}
                />
              </div>
              {row.temporary && (
                <Field label={tr('admin', 'until')} className="w-40">
                  <DatePicker value={row.until} disabled={editingSelf} onChange={(value) => updateRow(row.rowKey, { until: value })} />
                </Field>
              )}
              {!editingSelf && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={tr('admin', 'remove_this_role')}
                  onClick={() => onChange(rows.filter((item) => item.rowKey !== row.rowKey))}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
